/**
 * Does the course this screen was OPENED for disagree with the course the loaded work item is
 * actually in?
 *
 * A review screen is addressed by work item — `/queue/lesson/<workItemId>` — and the work item id is
 * globally unique, so the course in the query string is *context carried along from the queue*
 * (`lib/workItems.ts` `queueContextParams`), not part of the item's address. The two can therefore
 * disagree, and nothing downstream notices:
 *
 * - `POST /api/v1/queue/items/:workItemId/approve` sends **no course at all** (`api/client.ts`).
 * - The server's `assertItemCourseAccess` proves *"this reviewer may touch the item's own course"*,
 *   which is not the same claim as *"this item is in the course the reviewer was looking at"*. Once
 *   one person holds grants for Level I, II and III, that check passes for all of them.
 *
 * So the only place this can be caught today is here, before the decision controls are rendered.
 *
 * **It hard-stops; it does not redirect.** Silently moving the reviewer to the item's own course
 * turns a bad link into an unannounced course switch — he came to review Level II, sees a plausible
 * lesson, and approves Level III. A redirect implemented in an effect is worse still, because there
 * is one rendered frame in which a Level III approve button is live under a Level II heading.
 *
 * It is inert today: every work item in the queue is course `12b` and every queue link carries
 * `courseKey=12b`. It exists because the first Level II import is what makes it reachable.
 */
export interface CourseMismatch {
  /** The course the URL said this screen was about. */
  readonly requestedCourseKey: string;
  /** The course the loaded work item is actually in — immutable, frozen at import. */
  readonly itemCourseKey: string;
}

/**
 * `null` when there is nothing to complain about: no course was carried, the item has no course, or
 * the two agree. Comparison is exact — course keys are opaque handles (`12b`, `350`), never parsed.
 */
export function detectCourseMismatch(
  requestedCourseKey: string | null | undefined,
  itemCourseKey: string | null | undefined,
): CourseMismatch | null {
  if (!requestedCourseKey || !itemCourseKey || requestedCourseKey === itemCourseKey) {
    return null;
  }

  return { requestedCourseKey, itemCourseKey };
}

/**
 * What the reviewer reads. It names both courses and states plainly that nothing was changed —
 * the reader must be able to tell "I am blocked" from "the page failed to load".
 */
export function courseMismatchMessage(mismatch: CourseMismatch): string {
  return (
    `This link is for course ${mismatch.requestedCourseKey}, but this work item belongs to course ` +
    `${mismatch.itemCourseKey}. Lesson and item numbers repeat across courses, so these are different ` +
    `pieces of content. Nothing has been changed and no decision can be taken here — go back to the ` +
    `queue for course ${mismatch.itemCourseKey} and open the item from there.`
  );
}

/**
 * A link that named NO course at all is not agreement — it is an absence, and it must be refused.
 *
 * `/queue/lesson/<workItemId>` addresses a work item, whose id is globally unique and encodes no
 * course. With no `?courseKey`, {@link detectCourseMismatch} returns `null` (there is nothing to
 * compare), so before this existed the screen rendered the item and every decision control while the
 * shell header named whichever course `resolveCourseSelection` had fallen back to. On a session
 * holding Level I and Level III grants, that is a Level III lesson under a Level I heading with a
 * live approve button — the exact wire-crossing the reviewer described, on the one screen where a
 * decision is actually taken.
 *
 * **Why a hard stop rather than a warning**, and why not a redirect:
 *
 * - A warning leaves the decision controls live. A reviewer who has been told twice, in writing,
 *   that we are too verbose is not the person to defend a decision with a banner.
 * - Filling the URL from `item.courseKey` and re-rendering would make the server's own check compare
 *   a value with itself and pass always — a vacuous guard with a reassuring log line. The declaration
 *   has to come from the reviewer's own action, which is what the link below is.
 * - The cost is one click on a rare link: every path the tool builds already carries the course
 *   (`lib/workItems.ts` `queueContextParams`, and `queue.service.ts`'s Slack deep links), so a bare
 *   item link is a hand-typed or long-stale one.
 */
export interface UndeclaredCourse {
  /** The course the item is actually in — immutable, frozen at import. */
  readonly itemCourseKey: string;
}

export function detectUndeclaredCourse(
  requestedCourseKey: string | null | undefined,
  itemCourseKey: string | null | undefined,
): UndeclaredCourse | null {
  if (requestedCourseKey || !itemCourseKey) {
    return null;
  }

  return { itemCourseKey };
}

/**
 * What the reviewer reads.
 *
 * The course key appears only after the sentence that says what it IS — "the course this work item
 * belongs to" — because an identifier is an address and never a description. The exam level is not
 * named here on purpose: only the API's catalog may declare a level, these screens do not load it,
 * and inventing one from the id is the mistake that makes `20b` read as Level II. The shell header
 * above this panel says "Level not declared by this link", which is the honest statement of what is
 * and is not known.
 *
 * `courseTitle` and `level` are accepted for a caller that already holds the catalog; today none does.
 */
export function undeclaredCourseMessage(
  undeclared: UndeclaredCourse,
  courseTitle?: string | null,
  level?: string | null,
): string {
  const named = courseTitle
    ? `${courseTitle} (course ${undeclared.itemCourseKey})`
    : `course ${undeclared.itemCourseKey}`;
  const levelPhrase = level ? ` — ${level}` : '';
  return (
    `This link did not say which course it was for, and lesson and item numbers repeat across ` +
    `courses — the same number is a different lesson in each. This work item belongs to ` +
    `${named}${levelPhrase}. Nothing has been changed and no decision can be taken here until the ` +
    `course is stated, so that whatever you decide records which course you were looking at.`
  );
}
